import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LocalizationService } from 'src/app/services/localization/localization.service';

@Component({
  selector: 'app-education',
  templateUrl: './education.component.html',
  styleUrls: ['./education.component.css'],
})
export class EducationComponent {
  @Input() formGroup!: FormGroup;
  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  degrees = [
    { value: 'high_school', label: 'High School' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'associate', label: 'Associate Degree' },
    { value: 'bachelor', label: "Bachelor's Degree" },
    { value: 'BSc', label: 'BSc' },
    { value: 'master', label: "Master's Degree" },
    { value: 'MSc', label: 'MSc' },
    { value: 'doctorate', label: 'Doctorate (PhD)' },
    { value: 'professional', label: 'Professional Degree' },
    { value: 'certificate', label: 'Certificate' },
  ];
  userLang: any = 'en';
  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private localizationService: LocalizationService
  ) {
    this.userLang = this.localizationService.getUserLang();
  }

  ngOnInit(): void {
    if (!this.formGroup.get('education')) {
      this.formGroup.addControl('education', this.fb.array([]));
    }

    const educationArray = this.formGroup.get('education') as FormArray;

    // إذا كان فيه بيانات في الـ array، ما نضيفش entry جديدة
    if (educationArray.length === 0) {
      this.addEducation();
    } else {
      // Setup watchers for existing entries
      educationArray.controls.forEach((control, index) => {
        this.setupIsCurrentWatcher(control as FormGroup);
      });
    }
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.userLang = event.lang;
    });
  }

  ngAfterViewInit() {
    // تأكد من أن الـ validators شغالة
    setTimeout(() => {
      this.education.updateValueAndValidity();
    }, 100);
  }

  get education(): FormArray {
    return this.formGroup.get('education') as FormArray;
  }

  addEducation(): void {
    const educationGroup = this.fb.group({
      degree: [''],
      major: ['', [Validators.required, Validators.minLength(2)]],
      university: ['', [Validators.required, Validators.minLength(2)]],
      grade: [''],
      endDate: ['', Validators.required],
      details: [''],
      isCurrent: [false],
    });

    this.setupIsCurrentWatcher(educationGroup);
    this.education.push(educationGroup);
  }

  private setupIsCurrentWatcher(educationGroup: FormGroup): void {
    educationGroup.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
      const endDateControl = educationGroup.get('endDate');
      if (isCurrent) {
        endDateControl?.clearValidators();
        endDateControl?.setValue('');
      } else {
        endDateControl?.setValidators([Validators.required]);
      }
      endDateControl?.updateValueAndValidity();
    });
  }

  removeEducation(index: number): void {
    if (this.education.length > 1) {
      this.education.removeAt(index);
    }
  }

  onNext() {
    // التأكد من وجود entry واحد على الأقل
    if (this.education.length === 0) {
      this.addEducation();
      // alert('Please add at least one education entry');
      return;
    }

    // Mark all fields as touched للـ validation
    this.education.controls.forEach((ctrl) => {
      Object.keys((ctrl as FormGroup).controls).forEach((key) => {
        const control = (ctrl as FormGroup).get(key);
        control?.markAsTouched();
      });
    });

    // Check if at least one entry is valid
    const hasAtLeastOneValid = this.education.controls.some((ctrl) => {
      const group = ctrl as FormGroup;
      const major = group.get('major');
      const university = group.get('university');
      const endDate = group.get('endDate');
      const isCurrent = group.get('isCurrent')?.value;

      // If isCurrent is true, endDate is not required
      if (isCurrent) {
        return major?.valid && university?.valid;
      }

      return major?.valid && university?.valid && endDate?.valid;
    });

    if (!hasAtLeastOneValid) {
      const firstInvalidIndex = this.education.controls.findIndex((ctrl) => {
        const group = ctrl as FormGroup;
        const major = group.get('major');
        const university = group.get('university');
        const endDate = group.get('endDate');
        const isCurrent = group.get('isCurrent')?.value;

        if (isCurrent) {
          return !major?.valid || !university?.valid;
        }

        return !major?.valid || !university?.valid || !endDate?.valid;
      });

      if (firstInvalidIndex !== -1) {
        setTimeout(() => {
          const element =
            document.querySelectorAll('.card-data')[firstInvalidIndex];
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }

      return;
    }

    // Remove empty entries before proceeding
    for (let i = this.education.length - 1; i >= 0; i--) {
      const group = this.education.at(i) as FormGroup;
      const major = group.get('major')?.value;
      const university = group.get('university')?.value;

      if (!major && !university) {
        this.education.removeAt(i);
      }
    }

    this.next.emit();
  }

  onPrevious() {
    this.previous.emit();
  }

  isFieldInvalid(index: number, fieldName: string): boolean {
    const control = this.education.at(index).get(fieldName);
    return !!(control && control.invalid && control.touched);
  }

  getFieldError(index: number, fieldName: string): string | null {
    const control = this.education.at(index).get(fieldName);
    if (control && control.errors && control.touched) {
      if (control.errors['required']) return `${fieldName} is required`;
      if (control.errors['minlength'])
        return `${fieldName} must be at least ${control.errors['minlength'].requiredLength} characters`;
    }
    return null;
  }
}
